import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSignIn } from '@clerk/clerk-react'

export default function WorkOSCallback() {
    const [error, setError] = useState('')
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const { signIn, setActive, isLoaded } = useSignIn()
    const hasCalled = useRef(false)

    useEffect(() => {
        if (!isLoaded || hasCalled.current) return
        hasCalled.current = true

        const authenticate = async () => {
            try {
                // ── Step 1: Extract Clerk ticket from URL hash ─────────────────
                // Backend puts it here: /auth/workos/callback?round=xxx#__clerk_ticket=xxx
                const hash = window.location.hash
                const ticketMatch = hash.match(/__clerk_ticket=([^&]+)/)
                const ticket = ticketMatch?.[1]

                // ── Step 2: Extract custom data from query params ──────────────
                // These came from ProjectA via state param, decoded by backend
                const round = searchParams.get('round') || 'hr-round'
                const company = searchParams.get('company') || ''
                const jobTitle = searchParams.get('jobTitle') || ''
                const language = searchParams.get('language') || 'en'

                if (!ticket) {
                    throw new Error('No authentication ticket found in URL')
                }

                // ── Step 3: Consume Clerk ticket → create session ──────────────
                console.log('[WorkOSCallback] Consuming Clerk ticket...')
                const result = await signIn.create({
                    strategy: 'ticket',
                    ticket,
                })

                // ── Step 4: Set active Clerk session ──────────────────────────
                await setActive({ session: result.createdSessionId })
                console.log('[WorkOSCallback] Clerk session created successfully')

                // ── Step 5: Clean ticket from URL (security) ──────────────────
                window.history.replaceState({}, '', window.location.pathname + window.location.search)

                // ── Step 6: Redirect to interview page with custom data ────────
                console.log('[WorkOSCallback] Redirecting to interview...')
                navigate(
                    `/interview?round=${round}&company=${encodeURIComponent(company)}&jobTitle=${encodeURIComponent(jobTitle)}&language=${encodeURIComponent(language)}`,
                    { replace: true }
                )

            } catch (err) {
                console.error('[WorkOSCallback] Authentication error:', err)

                // If session already exists just redirect
                if (err.errors?.[0]?.code === 'session_exists') {
                    console.log('[WorkOSCallback] Session already exists, redirecting...')
                    const round = searchParams.get('round') || 'hr-round'
                    const company = searchParams.get('company') || ''
                    const jobTitle = searchParams.get('jobTitle') || ''
                    const language = searchParams.get('language') || 'en'
                    navigate(
                        `/interview?round=${round}&company=${encodeURIComponent(company)}&jobTitle=${encodeURIComponent(jobTitle)}&language=${encodeURIComponent(language)}`,
                        { replace: true }
                    )
                    return
                }

                setError(err.message || 'Authentication failed')
            }
        }

        authenticate()
    }, [isLoaded, searchParams, signIn, setActive, navigate])

    // ── Error UI ───────────────────────────────────────────────────────────
    if (error) {
        return (
            <div style={{
                padding: '2rem',
                textAlign: 'center',
                maxWidth: '400px',
                margin: '100px auto'
            }}>
                <h2 style={{ color: '#ef4444', marginBottom: '1rem' }}>
                    Authentication Failed
                </h2>
                <p style={{ marginBottom: '1.5rem' }}>{error}</p>
                <button
                    onClick={() => navigate('/pricing')}
                    style={{
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '1rem'
                    }}
                >
                    Return to Home
                </button>
            </div>
        )
    }

    // ── Loading UI ─────────────────────────────────────────────────────────
    return (
        <div style={{
            padding: '2rem',
            textAlign: 'center',
            maxWidth: '400px',
            margin: '100px auto'
        }}>
            <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid #e5e7eb',
                borderTopColor: '#3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 1.5rem'
            }} />
            <h2 style={{ marginBottom: '0.5rem' }}>Signing you in...</h2>
            <p style={{ color: '#6b7280' }}>
                Please wait while we authenticate your account.
            </p>
            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    )
}